import { z } from "zod";
import { pageOf, pageQuerySchema } from "../common/pagination.js";
import { localDateSchema, moneySchema, quantitySchema, timestampSchema, uuidSchema, } from "../common/primitives.js";
/**
 * Akaryakıt stok maliyeti: hareketli ağırlıklı ortalama (Prompt 12 §4).
 *
 * Kapsam ayardır (`costScope`): `STATION_PRODUCT` → satır istasyon × ürün; `COMPANY_PRODUCT` → `stationId` NULL.
 * Birim maliyet 6 ondalıkla tutulur (4 ondalık, 30.000 L'lik dolumda kuruş sapması üretir). Her hareket
 * `fuel_cost_history`'ye önce / sonra durumuyla yazılır; geriye dönük düzeltmede eskiyen satırlar SİLİNMEZ,
 * `superseded = true` işaretlenir.
 */
export const FUEL_COST_MOVEMENT_KINDS = [
    "RECEIPT",
    "ISSUE_SALES",
    "ISSUE_LOSS",
    "SURPLUS",
    "COST_CORRECTION",
];
export const fuelCostMovementKindSchema = z.enum(FUEL_COST_MOVEMENT_KINDS);
export const FUEL_COST_MOVEMENT_KIND_LABELS = {
    RECEIPT: "Giriş (dolum)",
    ISSUE_SALES: "Çıkış (satış)",
    ISSUE_LOSS: "Çıkış (fire)",
    SURPLUS: "Sayım fazlası",
    COST_CORRECTION: "Maliyet düzeltmesi",
};
export const FUEL_COST_SOURCE_TYPES = [
    "DELIVERY_LINE",
    "DAILY_CLOSING",
    "RECONCILIATION",
    "RECALCULATION",
    "OPENING",
];
export const fuelCostSourceTypeSchema = z.enum(FUEL_COST_SOURCE_TYPES);
export const FUEL_COST_SOURCE_TYPE_LABELS = {
    DELIVERY_LINE: "Dolum satırı",
    DAILY_CLOSING: "Gün kapanışı",
    RECONCILIATION: "Mutabakat",
    RECALCULATION: "Yeniden hesaplama",
    OPENING: "Açılış stoğu",
};
/** ₺/L, 6 ondalık dizge. */
export const unitCostSchema = z
    .string()
    .regex(/^\d{1,12}(\.\d{1,6})?$/, "Birim maliyet en fazla 6 ondalık basamaklı olmalıdır")
    .describe('Birim maliyet (₺/L, dizge, 6 ondalık). Örn. "41.235400"');
export const fuelStockCostSchema = z.object({
    id: uuidSchema,
    companyId: uuidSchema,
    stationId: uuidSchema.nullable().describe("Kapsam COMPANY_PRODUCT ise null"),
    productId: uuidSchema,
    productName: z.string(),
    quantityL: quantitySchema.describe("Ölçüm gecikmesiyle geçici NEGATİF olabilir"),
    averageUnitCost: unitCostSchema,
    /** Eldeki miktarın ortalama maliyetle değeri (kuruş). */
    stockValue: moneySchema,
    updatedAt: timestampSchema,
    version: z.number().int(),
});
export const listFuelStockCostsQuerySchema = z.object({
    companyId: uuidSchema,
    stationId: uuidSchema.optional(),
    productId: uuidSchema.optional(),
});
export const fuelStockCostListSchema = z.object({ items: z.array(fuelStockCostSchema) });
export const fuelCostHistorySchema = z.object({
    id: uuidSchema,
    stockCostId: uuidSchema,
    seq: z.number().int(),
    effectiveAt: timestampSchema,
    businessDate: localDateSchema,
    kind: fuelCostMovementKindSchema,
    sourceType: fuelCostSourceTypeSchema,
    sourceId: uuidSchema,
    quantityL: quantitySchema.describe("Giriş +, çıkış −"),
    unitCost: unitCostSchema,
    amount: moneySchema,
    quantityAfterL: quantitySchema,
    averageAfter: unitCostSchema,
    superseded: z.boolean(),
    createdAt: timestampSchema,
});
const queryBoolean = z.enum(["true", "false"]).transform((value) => value === "true");
export const listFuelCostHistoryQuerySchema = pageQuerySchema.extend({
    includeSuperseded: queryBoolean.optional().describe("Varsayılan: eskiyen satırlar gizlenir"),
    from: timestampSchema.optional(),
    to: timestampSchema.optional(),
});
export const fuelCostHistoryPageSchema = pageOf(fuelCostHistorySchema);
export const stockCostParamsSchema = z.object({ id: uuidSchema });
/**
 * Açılış stoğu: sisteme geçişte tanklarda bulunan yakıt. Kapsamın (istasyon × ürün ya da şirket × ürün) İLK hareketi olarak
 * girilir; kapsamda başka hareket varsa reddedilir. Muhasebe fişi üretmez (153'ün açılışı muhasebe açılış fişindedir).
 */
export const createFuelOpeningStockSchema = z.object({
    stationId: uuidSchema,
    productId: uuidSchema,
    quantityL: quantitySchema.refine((value) => Number(value) > 0, "Miktar sıfırdan büyük olmalıdır"),
    unitCost: unitCostSchema.describe("₺/L — ÖTV dâhil, KDV hariç"),
    asOf: timestampSchema.optional().describe("Stoğun ölçüldüğü an (varsayılan: şimdi; gelecekte olamaz)"),
});
//# sourceMappingURL=costs.js.map